import React from "react";
import AsyncSelect from "react-select/async";
import CustomerPicker from "../../../sales/components/CustomerPicker";
import EmployeePicker from "../../../users/components/EmployeePicker";
import { useLazyGetProductsQuery } from "../../../masters/products/productsApi";
import { useGetVehiclesQuery } from "../../../masters/vehicles/vehiclesApi";
import VehicleTypeSelect from "../../../masters/vehicles/components/VehicleTypeSelect";
import { vehicleTypeLabel } from "../../../masters/vehicles/vehicleTypes";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";

/**
 * Product picker for this report.
 *
 * Deliberately branch-scoped rather than using /masters/products/
 * search-all-branches: that endpoint's ProductLinkSummaryRecord doesn't carry
 * RecordGuid, and the report needs RecordGuid because Product_Code isn't unique
 * even within one branch (a real duplicate pair once 500'd the product Edit
 * route). Adding a column to that shared proc's SELECT list would break the
 * currently-deployed binary's Dapper binding, so instead this reuses the
 * branch-scoped list, which already returns RecordGuid.
 *
 * Nothing is lost by scoping the PICKER to a branch: the report itself still
 * resolves the product's cross-branch identities through App_Product_Links, so
 * picking it in its home branch still reports every branch it moved in.
 */
const ProductByGuidPicker = ({ branchId, value, onChange }) => {
  const [triggerSearch] = useLazyGetProductsQuery();

  const loadOptions = async (inputValue) => {
    if (!branchId || !inputValue || inputValue.trim().length < 2) return [];
    const { data = [] } = await triggerSearch({ branchId, search: inputValue.trim() });
    return data
      .filter((p) => p.recordGuid)
      .map((p) => ({
        value: p.recordGuid,
        label: `${p.productName}${p.productCode != null ? ` · ${p.productCode}` : ""}`,
        product: p,
      }));
  };

  return (
    <AsyncSelect
      classNamePrefix="react-select"
      cacheOptions
      isClearable
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      placeholder={branchId ? "Search product by name..." : "Pick a branch first"}
      isDisabled={!branchId}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? "No matching products" : "Type at least 2 characters"
      }
    />
  );
};

/**
 * Vehicle picker. No vehicle picker existed anywhere in the app - the Vehicle
 * Maintenance Report uses a plain <select> off useGetVehiclesQuery - so this is
 * a real react-select one built to the same option shape as the other pickers,
 * for reuse. Vehicles are few and RegNo is globally unique, so the whole list
 * is loaded once and filtered client-side rather than hitting a search endpoint.
 *
 * Type filter (Batch191): a Vehicle Type <select> above the search narrows the
 * options to that type (the same ?vehicleType= the reports use), and every
 * option shows its type after the registration. Changing the type clears a
 * pick that no longer fits.
 */
const VehiclePicker = ({ value, onChange, branchId }) => {
  const [vehicleType, setVehicleType] = React.useState("");
  // Header branch (2026-10-02): only that branch's vehicles; null = every branch the caller has.
  const { data: vehicles = [], isLoading } = useGetVehiclesQuery({
    vehicleType: vehicleType || undefined,
    branchId: branchId ?? undefined,
  });

  const options = React.useMemo(
    () =>
      (vehicles || []).map((v) => ({
        value: v.vehicleId,
        label: `${v.regNo}${v.brand ? ` · ${v.brand}` : ""}${v.model ? ` ${v.model}` : ""} · ${vehicleTypeLabel(v.vehicleType)}`,
        vehicle: v,
      })),
    [vehicles]
  );

  const onPickType = (type) => {
    setVehicleType(type);
    if (value && type && value.vehicle?.vehicleType !== type) onChange(null);
  };

  const loadOptions = async (inputValue) => {
    const q = (inputValue || "").trim().toLowerCase();
    if (!q) return options.slice(0, 25);
    return options.filter((o) => o.label.toLowerCase().includes(q));
  };

  return (
    <div className="d-flex flex-column gap-2">
      <VehicleTypeSelect className="form-select form-select-sm" value={vehicleType} onChange={onPickType} allLabel="All vehicle types" />
      <AsyncSelect
        key={`${vehicleType}|${branchId ?? "all"}`}
        classNamePrefix="react-select"
        defaultOptions={options.slice(0, 25)}
        isClearable
        isLoading={isLoading}
        loadOptions={loadOptions}
        value={value}
        onChange={onChange}
        placeholder={vehicleType ? `Search ${vehicleTypeLabel(vehicleType)} vehicles...` : "Search vehicle by registration..."}
        noOptionsMessage={() => "No matching vehicles"}
      />
    </div>
  );
};

/**
 * Switches the picker to whichever master type is selected, and hands back a
 * normalised selection the page can send straight to the endpoint.
 */
// `branchId` is the header branch (null = All): the customer and vehicle searches
// keep to it, and the product search uses it instead of its own branch select,
// which is only offered when the header is on All branches.
const OneStopMasterPicker = ({ masterType, selection, onSelect, productBranchId, setProductBranchId, branchId }) => {
  const { availableBranches } = useBranchFilter();

  if (masterType === "CUSTOMER") {
    return (
      <CustomerPicker
        key={branchId ?? "all"}
        branchId={branchId}
        isClearable
        value={selection}
        onChange={(opt) =>
          onSelect(
            opt
              ? {
                  ...opt,
                  query: { custId: opt.customer.custId, customerBranchId: opt.customer.branchId },
                }
              : null
          )
        }
      />
    );
  }

  if (masterType === "EMPLOYEE") {
    return (
      <EmployeePicker
        value={selection}
        onChange={(opt) =>
          onSelect(opt ? { ...opt, query: { employeeRecordGuid: opt.value } } : null)
        }
      />
    );
  }

  if (masterType === "PRODUCT") {
    return (
      <div className="d-flex flex-column gap-2">
        {branchId == null && (
          <select
            className="form-select form-select-sm"
            value={productBranchId ?? ""}
            onChange={(e) => {
              setProductBranchId(e.target.value ? Number(e.target.value) : null);
              onSelect(null);
            }}
          >
            <option value="">Select the product&rsquo;s branch...</option>
            {(availableBranches || []).map((b) => (
              <option key={b.branchId} value={b.branchId}>
                {b.branchName || b.branchCode}
              </option>
            ))}
          </select>
        )}
        <ProductByGuidPicker
          key={productBranchId ?? "none"}
          branchId={productBranchId}
          value={selection}
          onChange={(opt) =>
            onSelect(opt ? { ...opt, query: { productRecordGuid: opt.value } } : null)
          }
        />
      </div>
    );
  }

  return (
    <VehiclePicker
      branchId={branchId}
      value={selection}
      onChange={(opt) => onSelect(opt ? { ...opt, query: { vehicleId: opt.value } } : null)}
    />
  );
};

export default OneStopMasterPicker;
